import React from "react";
import { InlineMath } from "react-katex";
import { Rich } from "./Rich";

const PAGE = { width: 760, padding: "6px 10px", background: "#fff", color: "#1f2937", fontSize: 11, lineHeight: 1.4, fontFamily: "Georgia, 'Times New Roman', serif" };
const H = ({ children, color }) => <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color, margin: "8px 0 4px", borderBottom: `1px solid ${color}`, paddingBottom: 2 }}>{children}</div>;

// Text-heavy formulas become wrapped prose with inline math so nothing is clipped.
const asProse = (latex) => ("$" + latex.replace(/\\text\{([^{}]*)\}/g, "$$$1$$") + "$").replace(/\$\s*\$/g, " ");
const isTexty = (latex) => latex.includes("\\text") && latex.length > 50;

const collect = (notes, type) => notes.topics.flatMap((t) => t.blocks.filter((b) => b.type === type));

export const RevisionSheet = ({ notes }) => {
  const formulas = collect(notes, "formula");
  const theorems = collect(notes, "theorem");
  const revision = collect(notes, "revision").flatMap((b) => b.items);
  const mistakes = collect(notes, "mistake").slice(0, 5);
  const exam = collect(notes, "exam_focus").flatMap((b) => b.items).slice(0, 6);
  const seen = new Set();
  const key = (f) => f.title.toLowerCase().replace(/[^a-z]/g, "");
  const uniqFormulas = formulas.filter((f) => !seen.has(f.latex) && !seen.has(key(f)) && seen.add(f.latex) && seen.add(key(f))).slice(0, 14);

  return (
    <div className="pdf-topic pdf-item" style={PAGE} data-testid="revision-sheet">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", borderBottom: "2px solid #1f2937", paddingBottom: 4 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>{notes.chapter_title} — Revision Sheet</div>
        <div style={{ fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "#9d174d" }}>Gyaan Maths · Class {notes.class_level}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 14 }}>
        <div>
          <H color="#b45309">Key formulas</H>
          {uniqFormulas.map((f) => (
            <div key={f.id} className="pdf-avoid" style={{ marginBottom: 2 }}>
              <div style={{ fontSize: 10, fontWeight: 700 }}><Rich>{f.title}</Rich></div>
              <div style={{ fontSize: 11, padding: "1px 0 3px 8px" }}>
                {isTexty(f.latex) ? <Rich>{asProse(f.latex)}</Rich> : <InlineMath math={`\\displaystyle ${f.latex}`} renderError={() => <code>{f.latex}</code>} />}
              </div>
            </div>
          ))}
          {theorems.length > 0 && <H color="#6d28d9">Theorems</H>}
          {theorems.slice(0, 6).map((t) => (
            <div key={t.id} style={{ marginBottom: 4 }}><b><Rich>{t.title}</Rich>:</b> <Rich>{t.statement}</Rich></div>
          ))}
        </div>
        <div>
          <H color="#047857">Must remember</H>
          <ol style={{ paddingLeft: 16, margin: 0, listStyle: "decimal" }}>{revision.map((r, i) => <li key={i} style={{ marginBottom: 2 }}><Rich>{r}</Rich></li>)}</ol>
          {mistakes.length > 0 && <H color="#b91c1c">Avoid these mistakes</H>}
          {mistakes.map((m) => <div key={m.id} style={{ marginBottom: 3 }}>✗ <Rich>{m.wrong}</Rich> <br />✓ <Rich>{m.right}</Rich></div>)}
          {exam.length > 0 && <H color="#be185d">Exam focus</H>}
          <ul style={{ paddingLeft: 16, margin: 0, listStyle: "disc" }}>{exam.map((e, i) => <li key={i}><Rich>{e}</Rich></li>)}</ul>
        </div>
      </div>
    </div>
  );
};
